/**
 * Provider Status Hook
 * Polls AI provider status pages and exposes results.
 * Can be used in AppLayout (global banner) and Settings (per-card indicators).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { settingsAPI } from '../services/api';
import type { ProviderStatusResponse } from '../services/api';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseProviderStatusReturn {
  providerStatus: ProviderStatusResponse | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useProviderStatus(apiKey: string | null): UseProviderStatusReturn {
  const [providerStatus, setProviderStatus] = useState<ProviderStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!apiKey) return;

    setIsLoading(true);
    try {
      const data = await settingsAPI.getProviderStatus(apiKey);
      setProviderStatus(data);
    } catch {
      // Silently fail — don't spam user with status-check errors
    } finally {
      setIsLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    if (!apiKey) return;

    // Fetch immediately on mount
    fetchStatus();

    // Poll every 5 minutes
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [apiKey, fetchStatus]);

  return { providerStatus, isLoading, refresh: fetchStatus };
}
